import { Reference } from '@approvalmax/types';
import { DropdownEditor, FocusImplementer, NumberEditor, TextAreaEditor } from '@approvalmax/ui';
import { intl } from '@approvalmax/utils';
import { backend, domain } from 'modules/data';
import { QBooksAccount, QBooksJournalEntryLineAmountType, QBooksPayee, QBooksPayeeType } from 'modules/data/domain';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { ReadOnlyCell } from 'pages/requestForm/components';
import { getQBooksContext } from 'pages/requestForm/selectors/qbooks';
import { ColumnDefinition } from 'pages/requestForm/selectors/types/ColumnDefinition';
import { useMemo } from 'react';

import { ExpandedJournalEntryLine, TaxCodeListItem } from '../../QBooksJournalEntryRequestForm.types';
import { messages } from './JournalEntryLines.messages';
import { DropdownEditorContainer } from './JournalEntryLines.styles';
import { UseTableDefProps } from './JournalEntryLines.types';

export const useTableDef = (props: UseTableDefProps) => {
    const {
        payees,
        taxCodeList,
        showErrors,
        showErrorsOnEmptyLine,
        showTaxColumn = true,
        isClassFieldRequired,
        isDepartmentFieldRequired,
        onLineFieldUpdate,
    } = props;

    const qbooksContext = useSelector(getQBooksContext);

    const { accounts, payeeTypes, classes, hasClassFeature, departments, hasDepartmentFeature, departmentField } =
        qbooksContext;

    const tableDef = useMemo<ColumnDefinition<ExpandedJournalEntryLine>[]>(
        () => [
            {
                id: 'number',
                name: messages.numberColName,
                fixedWidth: 60,
                cell: (_li, _options, lineItemIndex) => {
                    return <ReadOnlyCell text={String(lineItemIndex + 1)} />;
                },
            },
            {
                id: 'account',
                name: messages.accountColName,
                flexGrow: 1,
                readonly: () => false,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (!li.isEmpty && !li.account) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.account?.text ?? ''} />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <DropdownEditorContainer>
                                    <PlainDataProvider items={accounts}>
                                        <DropdownEditor
                                            ref={registerFocusable}
                                            invalid={invalid}
                                            value={li.account || null}
                                            singleLinePerItem={false}
                                            onChange={(value: QBooksAccount | null) => {
                                                onLineFieldUpdate(li.id, 'account', value);
                                            }}
                                            placeholder={messages.defaultSelectPlaceholder}
                                            theme='transparent'
                                        />
                                    </PlainDataProvider>
                                </DropdownEditorContainer>
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'debit',
                name: messages.debitColName,
                alignRight: true,
                flexGrow: 1,
                readonly: () => false,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (!li.isEmpty && typeof li.amount !== 'number') {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell
                            text={
                                li.amount !== null && li.amountType === QBooksJournalEntryLineAmountType.Debit
                                    ? intl.formatNumber(li.amount, 'auto')
                                    : ''
                            }
                        />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <>
                                    <NumberEditor
                                        ref={registerFocusable}
                                        allowFloat
                                        precision={2}
                                        invalid={invalid}
                                        value={
                                            li.amount !== null &&
                                            li.amountType === QBooksJournalEntryLineAmountType.Debit
                                                ? li.amount
                                                : null
                                        }
                                        onChange={(newValue: number | null) => {
                                            const isNegativeValue = newValue !== null && newValue < 0;

                                            const newAmount = isNegativeValue ? newValue * -1 : newValue;

                                            onLineFieldUpdate(li.id, 'amount', newAmount);
                                            onLineFieldUpdate(
                                                li.id,
                                                'amountType',
                                                isNegativeValue
                                                    ? QBooksJournalEntryLineAmountType.Credit
                                                    : QBooksJournalEntryLineAmountType.Debit
                                            );
                                        }}
                                        placeholder={messages.defaultInputPlaceholder}
                                        theme='transparent'
                                        align='right'
                                        displayFormat='currency-auto'
                                        allowCalculation
                                    />
                                </>
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'credit',
                name: messages.creditColName,
                alignRight: true,
                flexGrow: 1,
                readonly: () => false,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (!li.isEmpty && typeof li.amount !== 'number') {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell
                            text={
                                li.amount !== null && li.amountType === QBooksJournalEntryLineAmountType.Credit
                                    ? intl.formatNumber(li.amount, 'auto')
                                    : ''
                            }
                        />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <>
                                    <NumberEditor
                                        ref={registerFocusable}
                                        allowFloat
                                        precision={2}
                                        invalid={invalid}
                                        value={
                                            li.amount !== null &&
                                            li.amountType === QBooksJournalEntryLineAmountType.Credit
                                                ? li.amount
                                                : null
                                        }
                                        onChange={(newValue: number | null) => {
                                            const isNegativeValue = newValue !== null && newValue < 0;

                                            const newAmount = isNegativeValue ? newValue * -1 : newValue;

                                            onLineFieldUpdate(li.id, 'amount', newAmount);
                                            onLineFieldUpdate(
                                                li.id,
                                                'amountType',
                                                isNegativeValue
                                                    ? QBooksJournalEntryLineAmountType.Debit
                                                    : QBooksJournalEntryLineAmountType.Credit
                                            );
                                        }}
                                        placeholder={messages.defaultInputPlaceholder}
                                        theme='transparent'
                                        align='right'
                                        displayFormat='currency-auto'
                                        allowCalculation
                                    />
                                </>
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'description',
                name: messages.descriptionColName,
                flexGrow: 2,
                readonly: () => false,
                invalid: () => showErrors && showErrorsOnEmptyLine,
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.description || ''} multiline />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <TextAreaEditor
                                    ref={registerFocusable}
                                    invalid={invalid}
                                    value={li.description || ''}
                                    onChange={(value) => {
                                        onLineFieldUpdate(li.id, 'description', value);
                                    }}
                                    placeholder='Enter value'
                                    theme='transparent'
                                    changeOnBlur
                                />
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'payeeType',
                name: messages.payeeTypeColName,
                flexGrow: 1,
                readonly: () => false,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (li.account) {
                        const accountInfo = accounts.find((account) => account.id === li.account?.id);

                        if (accountInfo?.type === domain.QBooksAccountType.AccountsPayable) {
                            return li.payeeType?.id !== backend.IntegrationsQBooksPayeeTypes.Vendor;
                        }

                        if (accountInfo?.type === domain.QBooksAccountType.AccountsReceivable) {
                            return li.payeeType?.id !== backend.IntegrationsQBooksPayeeTypes.Customer;
                        }
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) =>
                    readonly ? (
                        <ReadOnlyCell text={li.payeeType?.text ?? ''} />
                    ) : (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <DropdownEditorContainer>
                                    <PlainDataProvider items={payeeTypes || []}>
                                        <DropdownEditor
                                            ref={registerFocusable}
                                            invalid={invalid}
                                            value={li.payeeType || null}
                                            singleLinePerItem={false}
                                            onChange={(value: QBooksPayeeType | null) => {
                                                onLineFieldUpdate(li.id, 'payeeType', value);
                                            }}
                                            placeholder={messages.defaultSelectPlaceholder}
                                            theme='transparent'
                                        />
                                    </PlainDataProvider>
                                </DropdownEditorContainer>
                            )}
                        </FocusImplementer>
                    ),
            },
            {
                id: 'name',
                name: messages.nameColName,
                flexGrow: 1,
                readonly: (li) => !li.payeeType,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    if (li.payeeType && !li.payee) {
                        return true;
                    }

                    return showErrorsOnEmptyLine;
                },
                cell: (li, { invalid, readonly }) => {
                    if (readonly) {
                        return <ReadOnlyCell text={li.payeeType?.text ?? ''} />;
                    }

                    let payeeList: Reference[] = [];

                    switch (li.payeeType?.id) {
                        case backend.IntegrationsQBooksPayeeTypes.Vendor:
                            payeeList = payees?.vendors ?? [];

                            break;

                        case backend.IntegrationsQBooksPayeeTypes.Customer:
                            payeeList = payees?.customers ?? [];

                            break;

                        case backend.IntegrationsQBooksPayeeTypes.Employee:
                            payeeList = payees?.employees ?? [];

                            break;
                    }

                    return (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <DropdownEditorContainer>
                                    <PlainDataProvider items={payeeList}>
                                        <DropdownEditor
                                            ref={registerFocusable}
                                            invalid={invalid}
                                            value={li.payee || null}
                                            singleLinePerItem={false}
                                            onChange={(value: QBooksPayee | null) => {
                                                onLineFieldUpdate(li.id, 'payee', value);
                                            }}
                                            placeholder={messages.defaultSelectPlaceholder}
                                            theme='transparent'
                                        />
                                    </PlainDataProvider>
                                </DropdownEditorContainer>
                            )}
                        </FocusImplementer>
                    );
                },
            },
            ...(showTaxColumn
                ? [
                      {
                          id: 'taxCode',
                          name: messages.taxCodeColName,
                          flexGrow: 1,
                          readonly: () => false,
                          invalid: () => showErrors && showErrorsOnEmptyLine,
                          cell: (li, { invalid, readonly }) =>
                              readonly ? (
                                  <ReadOnlyCell text={li.taxCode?.text ?? ''} />
                              ) : (
                                  <FocusImplementer>
                                      {({ registerFocusable }) => (
                                          <DropdownEditorContainer>
                                              <PlainDataProvider items={taxCodeList || []}>
                                                  <DropdownEditor
                                                      ref={registerFocusable}
                                                      invalid={invalid}
                                                      value={li.taxCode || null}
                                                      singleLinePerItem={false}
                                                      onChange={(value: TaxCodeListItem | null) => {
                                                          onLineFieldUpdate(li.id, 'taxCode', value);
                                                      }}
                                                      placeholder={messages.defaultSelectPlaceholder}
                                                      theme='transparent'
                                                  />
                                              </PlainDataProvider>
                                          </DropdownEditorContainer>
                                      )}
                                  </FocusImplementer>
                              ),
                      } as ColumnDefinition<ExpandedJournalEntryLine>,
                  ]
                : []),
            ...(hasDepartmentFeature
                ? [
                      {
                          id: 'department',
                          name: departmentField?.text || messages.departmentColName,
                          flexGrow: 1,
                          readonly: () => false,
                          invalid: (li) => {
                              if (!showErrors) {
                                  return false;
                              }

                              if (!li.isEmpty && isDepartmentFieldRequired && !li.department) {
                                  return true;
                              }

                              return showErrorsOnEmptyLine;
                          },
                          cell: (li, { invalid, readonly }) =>
                              readonly ? (
                                  <ReadOnlyCell text={li.department?.text ?? ''} />
                              ) : (
                                  <FocusImplementer>
                                      {({ registerFocusable }) => (
                                          <DropdownEditorContainer>
                                              <PlainDataProvider items={departments || []}>
                                                  <DropdownEditor
                                                      ref={registerFocusable}
                                                      invalid={invalid}
                                                      value={li.department || null}
                                                      singleLinePerItem={false}
                                                      onChange={(value: Reference | null) => {
                                                          onLineFieldUpdate(li.id, 'department', value);
                                                      }}
                                                      placeholder={messages.defaultSelectPlaceholder}
                                                      theme='transparent'
                                                  />
                                              </PlainDataProvider>
                                          </DropdownEditorContainer>
                                      )}
                                  </FocusImplementer>
                              ),
                      } as ColumnDefinition<ExpandedJournalEntryLine>,
                  ]
                : []),
            ...(hasClassFeature
                ? [
                      {
                          id: 'class',
                          name: messages.classColName,
                          flexGrow: 1,
                          readonly: () => false,
                          invalid: (li) => {
                              if (!showErrors) {
                                  return false;
                              }

                              if (!li.isEmpty && isClassFieldRequired && !li.class) {
                                  return true;
                              }

                              return showErrorsOnEmptyLine;
                          },
                          cell: (li, { invalid, readonly }) =>
                              readonly ? (
                                  <ReadOnlyCell text={li.class?.text ?? ''} />
                              ) : (
                                  <FocusImplementer>
                                      {({ registerFocusable }) => (
                                          <DropdownEditorContainer>
                                              <PlainDataProvider items={classes || []}>
                                                  <DropdownEditor
                                                      ref={registerFocusable}
                                                      invalid={invalid}
                                                      value={li.class || null}
                                                      singleLinePerItem={false}
                                                      onChange={(value: Reference | null) => {
                                                          onLineFieldUpdate(li.id, 'class', value);
                                                      }}
                                                      placeholder={messages.defaultSelectPlaceholder}
                                                      theme='transparent'
                                                  />
                                              </PlainDataProvider>
                                          </DropdownEditorContainer>
                                      )}
                                  </FocusImplementer>
                              ),
                      } as ColumnDefinition<ExpandedJournalEntryLine>,
                  ]
                : []),
        ],
        [
            accounts,
            payeeTypes,
            payees,
            taxCodeList,
            hasClassFeature,
            classes,
            hasDepartmentFeature,
            departments,
            departmentField,
            showErrors,
            showErrorsOnEmptyLine,
            showTaxColumn,
            isClassFieldRequired,
            isDepartmentFieldRequired,
            onLineFieldUpdate,
        ]
    );

    return tableDef;
};
